import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiSearch, FiDownload } from 'react-icons/fi';
import reportesAvanzadosService from '../services/reportesAvanzadosService';
import Loading from '../components/Loading';
import './PresupuestoVsRealPage.css';

const PresupuestoVsRealPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState('');
  const [areaId, setAreaId] = useState('');

  const cargar = async () => {
    try {
      setLoading(true);
      const filters = { anio };
      if (mes) filters.mes = mes;
      if (areaId) filters.area_id = areaId;
      const res = await reportesAvanzadosService.getPresupuestoVsReal(filters);
      setData(res.data);
    } catch { toast.error('Error al cargar reporte'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);
  const formatPct = (v) => `${(parseFloat(v || 0)).toFixed(1)}%`;

  const getBarColor = (pct) => {
    const p = parseFloat(pct || 0);
    if (p <= 80) return '#059669';
    if (p <= 100) return '#f59e0b';
    return '#dc2626';
  };

  const exportarExcel = async () => {
    try {
      const XLSX = (await import('xlsx')).default;
      const partidas = data?.partidas || [];
      const ws = XLSX.utils.json_to_sheet(partidas.map(p => ({
        Area: p.area_nombre || '-', Partida: p.partida_nombre || p.nombre, Presupuestado: p.presupuestado,
        Ejecutado: p.ejecutado, Diferencia: p.diferencia, '% Ejecucion': p.porcentaje_ejecucion,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Presupuesto vs Real');
      XLSX.writeFile(wb, `presupuesto_vs_real_${anio}.xlsx`);
    } catch { toast.error('Error al exportar'); }
  };

  const partidas = data?.partidas || data?.results || [];
  const totales = data?.totales || {};
  const mesesNombres = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  return (
    <div className="ppto-vs-real-page">
      <div className="page-header"><h1>Presupuesto vs Real</h1></div>

      <div className="selector-bar">
        <div className="form-group">
          <label>Ano</label>
          <select value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
            {[2024, 2025, 2026].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Mes</label>
          <select value={mes} onChange={e => setMes(e.target.value)}>
            <option value="">Acumulado Anual</option>
            {mesesNombres.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Area (ID)</label>
          <input type="number" value={areaId} onChange={e => setAreaId(e.target.value)} placeholder="Todas" />
        </div>
        <button className="btn-primary" onClick={cargar}><FiSearch /> Consultar</button>
        {data && <button className="btn-export" onClick={exportarExcel}><FiDownload /> Excel</button>}
      </div>

      {loading && <Loading />}

      {!loading && data && (
        <>
          {/* Resumen */}
          <div className="resumen-cards">
            <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
              <div className="stat-label">Presupuestado</div>
              <div className="stat-value">{formatCurrency(totales.presupuestado || partidas.reduce((s, p) => s + parseFloat(p.presupuestado || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
              <div className="stat-label">Ejecutado</div>
              <div className="stat-value">{formatCurrency(totales.ejecutado || partidas.reduce((s, p) => s + parseFloat(p.ejecutado || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#059669' }}>
              <div className="stat-label">Disponible</div>
              <div className="stat-value">{formatCurrency(totales.diferencia || partidas.reduce((s, p) => s + parseFloat(p.diferencia || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#6b7280' }}>
              <div className="stat-label">% Ejecucion Global</div>
              <div className="stat-value">{formatPct(totales.porcentaje_ejecucion || (() => {
                const pres = partidas.reduce((s, p) => s + parseFloat(p.presupuestado || 0), 0);
                const ejec = partidas.reduce((s, p) => s + parseFloat(p.ejecutado || 0), 0);
                return pres > 0 ? (ejec / pres) * 100 : 0;
              })())}</div>
            </div>
          </div>

          {/* Tabla */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Partida</th>
                  <th style={{ textAlign: 'right' }}>Presupuestado</th>
                  <th style={{ textAlign: 'right' }}>Ejecutado</th>
                  <th style={{ textAlign: 'right' }}>Diferencia</th>
                  <th style={{ width: 180 }}>% Ejecucion</th>
                </tr>
              </thead>
              <tbody>
                {partidas.length === 0 ? (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin datos para el periodo</td></tr>
                ) : partidas.map((p, i) => {
                  const pct = parseFloat(p.porcentaje_ejecucion || 0);
                  const diff = parseFloat(p.diferencia || 0);
                  return (
                    <tr key={i} className={p.es_area ? 'row-area' : ''}>
                      <td>{p.area_nombre || '-'}</td>
                      <td>{p.es_area ? <strong>{p.partida_nombre || p.nombre}</strong> : (p.partida_nombre || p.nombre)}</td>
                      <td className="mono right">{formatCurrency(p.presupuestado)}</td>
                      <td className="mono right">{formatCurrency(p.ejecutado)}</td>
                      <td className="mono right" style={{ color: diff >= 0 ? '#059669' : '#dc2626' }}>{formatCurrency(p.diferencia)}</td>
                      <td>
                        <div className="progress-container">
                          <div className="progress-bar" style={{ width: `${Math.min(pct, 100)}%`, background: getBarColor(pct) }}></div>
                          <span className="progress-label" style={{ color: getBarColor(pct) }}>{formatPct(pct)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {partidas.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="2" style={{ fontWeight: 700 }}>TOTAL</td>
                    <td className="mono right bold">{formatCurrency(totales.presupuestado || partidas.reduce((s, p) => s + parseFloat(p.presupuestado || 0), 0))}</td>
                    <td className="mono right bold">{formatCurrency(totales.ejecutado || partidas.reduce((s, p) => s + parseFloat(p.ejecutado || 0), 0))}</td>
                    <td className="mono right bold">{formatCurrency(totales.diferencia || partidas.reduce((s, p) => s + parseFloat(p.diferencia || 0), 0))}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PresupuestoVsRealPage;
